"use client";

import {
  AlertTriangle,
  Calendar,
  Gift,
  MapPin,
  Ticket,
  Users,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEventUpdates } from "@/pages/events/features/notifications/event-updates-context";

const notificationDetails = {
  venue_changed: {
    title: "Venue Changed",
    description: "Notify all attendees about the venue change",
    icon: MapPin,
    color: "bg-blue-500",
  },
  lineup_added: {
    title: "Lineup Added",
    description: "Announce new artists to the lineup",
    icon: Users,
    color: "bg-green-500",
  },
  event_added_v2: {
    title: "Event Added",
    description: "Notify followers about the new event",
    icon: Calendar,
    color: "bg-purple-500",
  },
  offer_added_v2: {
    title: "Discount/Giveaway Added",
    description: "Announce special offers and giveaways",
    icon: Gift,
    color: "bg-orange-500",
  },
  event_cancelled: {
    title: "Event Cancelled",
    description: "Notify all attendees about the cancellation",
    icon: XCircle,
    color: "bg-red-500",
  },
  tickets_on_sale: {
    title: "Tickets on Sale",
    description: "Announce ticket availability",
    icon: Ticket,
    color: "bg-indigo-500",
  },
  date_changed: {
    title: "Date Changed",
    description: "Notify all attendees about the date change",
    icon: Calendar,
    color: "bg-blue-500",
  },
};

export function UpdateConfirmationModal() {
  const {
    isConfirmationModalOpen,
    closeConfirmationModal,
    pendingUpdate,
    sendEventUpdates,
  } = useEventUpdates();

  if (!pendingUpdate) return null;

  const details = notificationDetails[pendingUpdate.type];
  const IconComponent = details.icon;

  const handleConfirm = () => {
    sendEventUpdates(pendingUpdate.type);
    closeConfirmationModal();
  };

  return (
    <Dialog
      open={isConfirmationModalOpen}
      onOpenChange={(open) => !open && closeConfirmationModal()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${details.color}`}>
              <IconComponent className="h-4 w-4 text-white" />
            </div>
            <div>
              <DialogTitle>Confirm Notification</DialogTitle>
              <DialogDescription>
                Review the notification details before sending
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium">Action to be triggered:</span>
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-3">
            <div>
              <h4 className="font-medium">{details.title}</h4>
              <p className="text-sm text-muted-foreground">
                {details.description}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={closeConfirmationModal}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} className="gap-2">
            <IconComponent className="h-4 w-4" />
            Accept & Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
